/**
 * locateHighlight — the "原文位置" jump's visual half. When a finding/citation
 * is located in a viewer (DocxPreview block, PdfViewer page, TxtPreview line),
 * the viewer calls `revealLocated(el)` to scroll it into view AND leave a
 * persistent primary marker on the matched region so the lawyer can audit it,
 * not just watch the pane scroll past.
 *
 * The highlight is sticky: it stays on the region until the NEXT locate call
 * clears it (auditing needs the mark to persist, not flash and vanish). Each
 * viewer passes its own most-granular element (DOCX block, PDF page wrapper,
 * TXT line); this util is DOM-shape agnostic.
 *
 * Style is injected once as a global rule (the viewers use inline styles and
 * don't share a CSS module), keyed off the primary design tokens so it reads as
 * one system with the rest of the workbench.
 */
const HIGHLIGHT_CLASS = "jerry-locate-highlight";
const STYLE_ID = "jerry-locate-highlight-style";

let lastHighlighted: HTMLElement | null = null;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.${HIGHLIGHT_CLASS} {
  background-color: var(--jerry-primary-bg, rgba(124, 58, 237, 0.18));
  box-shadow: inset 3px 0 0 var(--jerry-primary-600, #5F14E0);
  border-radius: 2px;
  scroll-margin-top: 72px;
  scroll-margin-bottom: 72px;
  animation: jerry-locate-pulse 1100ms ease-out 1;
  transition: background-color 180ms ease-out;
}
@keyframes jerry-locate-pulse {
  0% {
    background-color: var(--jerry-primary-glow, rgba(124, 58, 237, 0.45));
    box-shadow: inset 3px 0 0 var(--jerry-primary-700, #4C14AD),
                0 0 0 6px var(--jerry-primary-subtle, rgba(124, 58, 237, 0.2));
  }
  100% {
    background-color: var(--jerry-primary-bg, rgba(124, 58, 237, 0.18));
    box-shadow: inset 3px 0 0 var(--jerry-primary-600, #5F14E0);
  }
}`;
  document.head.appendChild(style);
}

/** Remove the highlight class from whatever element (if any) is currently
 * marked, so a new locate replaces rather than stacks. Safe to call when nothing
 * is highlighted. */
export function clearLocateHighlight(): void {
  if (lastHighlighted) {
    lastHighlighted.classList.remove(HIGHLIGHT_CLASS);
    lastHighlighted = null;
  }
}

/**
 * Scroll `el` into view and mark it as the located region. Clears any prior
 * highlight first. No-op when `el` is null (defensive; viewers pre-check).
 *
 * @param block scrollIntoView block alignment — "center" for paragraph/block
 * elements (default), "start" for full-page jumps (PdfViewer page wrapper).
 */
export function revealLocated(
  el: HTMLElement | null,
  block: ScrollLogicalPosition = "center",
): void {
  if (!el) return;
  ensureStyle();
  if (lastHighlighted && lastHighlighted !== el) {
    lastHighlighted.classList.remove(HIGHLIGHT_CLASS);
  }
  el.classList.add(HIGHLIGHT_CLASS);
  lastHighlighted = el;
  el.scrollIntoView({ behavior: "smooth", block });
}
